import { useMemo } from 'react';
import { TrackReferenceSystem } from '../utils/referenceSystem';

interface LTV {
    id: string;
    startPK: number;
    endPK: number;
    speed: number;
    reason: string;
    startDate: string;
    endDate: string;
    line?: 'Centrale' | 'Balagne';
}

interface Telemetry {
    currentPK: number | null;
    speed: number;
    distanceToNextLTV: number | null;
    nextLTV: LTV | null;
}

export function useTrainTelemetry(
    currentCoords: [number, number] | null,
    ltvList: LTV[],
    referenceSystem: TrackReferenceSystem,
    isTravelUp: boolean = true,
    isSensPair: boolean = false
): Telemetry {

    // 1. Calculer le PK actuel via le système de référence précis à partir des coordonnées GPS
    const currentPK = useMemo(() => {
        if (!currentCoords) return null;
        return Number(referenceSystem.coordToPK(currentCoords).toFixed(3));
    }, [currentCoords, referenceSystem]);

    // 2. Trouver la prochaine LTV et la distance
    const { distanceToNextLTV, nextLTV } = useMemo(() => {
        if (currentPK === null) return { distanceToNextLTV: null, nextLTV: null };

        let nearestLTV: LTV | null = null;
        let minDistance = Infinity;

        // Filtrer les LTVs pertinentes selon notre direction et notre sens ferroviaire
        const relevantLTVs = ltvList.filter(ltv => {
            const ltvLine = ltv.line;
            if (ltvLine) {
                if (ltvLine !== referenceSystem.lineName) {
                    return false;
                }
            } else {
                // Rétrocompatibilité : Filtrer si l'LTV est clairement hors de la plage de la Balagne
                const minPK = Math.min(ltv.startPK, ltv.endPK);
                const maxPK = Math.max(ltv.startPK, ltv.endPK);
                const inBalagneRange = maxPK <= 119.9 && minPK >= 46.7;
                if (referenceSystem.lineName === 'Balagne' && !inBalagneRange) {
                    return false;
                }
            }

            // Filtrage par Sens Pair / Sens Impair (si spécifié dans le motif de la LTV)
            const reasonUpper = (ltv.reason || '').toUpperCase();
            if (reasonUpper.includes('(SENS PAIR)') && !isSensPair) {
                return false; // LTV réservée au sens pair, alors qu'on est en sens impair
            }
            if (reasonUpper.includes('(SENS IMPAIR)') && isSensPair) {
                return false; // LTV réservée au sens impair, alors qu'on est en sens pair
            }

            const minPK = Math.min(ltv.startPK, ltv.endPK);
            const maxPK = Math.max(ltv.startPK, ltv.endPK);

            // Si on va vers les PK croissants (isTravelUp = true),
            // on ne garde que les LTVs dont la fin est DEVANT nous
            if (isTravelUp) {
                return maxPK >= currentPK - 0.050; // Garder une marge pour l'alerte de fin
            }
            // Si on va vers les PK décroissants (isTravelUp = false),
            // on ne garde que les LTVs dont le début est DEVANT nous
            else {
                return minPK <= currentPK + 0.050;
            }
        });

        for (const ltv of relevantLTVs) {
            const minPK = Math.min(ltv.startPK, ltv.endPK);
            const maxPK = Math.max(ltv.startPK, ltv.endPK);

            let distM = Infinity;

            if (isTravelUp) {
                // PK croissant : On cherche les zones dont le début (minPK) est devant nous
                if (currentPK < minPK) {
                    distM = (minPK - currentPK) * 1000;
                } else if (currentPK <= maxPK) {
                    distM = 0; // On est dedans
                } else {
                    // On a dépassé la zone (on est à un PK > maxPK)
                    distM = Infinity;
                }
            } else {
                // PK décroissant : On cherche les zones dont le début (maxPK) est devant nous
                if (currentPK > maxPK) {
                    distM = (currentPK - maxPK) * 1000;
                } else if (currentPK >= minPK) {
                    distM = 0; // On est dedans
                } else {
                    // On a dépassé la zone (on est à un PK < minPK)
                    distM = Infinity;
                }
            }

            if (distM < minDistance) {
                minDistance = distM;
                nearestLTV = ltv;
            }
        }

        return {
            distanceToNextLTV: nearestLTV ? minDistance : null,
            nextLTV: nearestLTV
        };
    }, [currentPK, ltvList, isTravelUp]);

    return {
        currentPK,
        speed: 48, // Simulé
        distanceToNextLTV,
        nextLTV
    };
}
